/**
 * Tipo di collegamento di rete verso LiveKit, letto dalle statistiche WebRTC (getStats).
 *
 * Si guarda la coppia di candidati ICE selezionata e il suo candidato locale:
 *   - `relay` (TURN)                          -> relay, via `turn-udp` | `turn-tcp` | `turn-tls` (relayProtocol);
 *   - protocollo `tcp` (ICE/TCP, UDP bloccato) -> relay, via `tcp`;
 *   - `host` / `srflx` / `prflx` con `udp`     -> direct, via `udp`.
 *
 * Da dove vengono le statistiche (vedi `collectNetPath`), in ordine di preferenza:
 *   1. API pubblica di livekit-client: `LocalTrack.getRTCStatsReport()` sulla traccia del microfono
 *      pubblicata (= `RTCRtpSender.getStats()`: per la "stats selection algorithm" il report contiene anche
 *      transport, candidate-pair e candidati della connessione che porta il nostro audio);
 *   2. `room.engine.pcManager.publisher.getStats()`: PeerConnection di pubblicazione intera (in modalità a
 *      singola PeerConnection, `publisher-only`, è l'unica e porta anche l'audio ricevuto). Serve quando non
 *      pubblichiamo (microfono negato) o il browser non riporta la coppia nel report del sender;
 *   3. `room.engine.pcManager.subscriber.getStats()`: PeerConnection di ricezione (modalità a due PC).
 * `engine` e `pcManager` sono campi pubblici nelle tipizzazioni di livekit-client 2.x ma non documentati: si
 * accede in modo difensivo, così un cambio interno fa solo sparire l'icona, senza errori.
 */
import type { NetKind, NetVia } from '../api/protocol';

export interface NetPath {
  net: NetKind;
  via: NetVia;
}

/** Quello che serve di un RTCStatsReport: iterabile su oggetti statistica (Map o array nei test). */
export type StatsLike = { forEach(cb: (s: any) => void): void } | Iterable<any>;

type Stat = Record<string, unknown> & { id?: string; type?: string };

function toList(report: StatsLike): Stat[] {
  const out: Stat[] = [];
  if (typeof (report as { forEach?: unknown }).forEach === 'function') {
    (report as { forEach(cb: (s: any) => void): void }).forEach((s) => {
      if (s && typeof s === 'object') out.push(s as Stat);
    });
  } else {
    for (const s of report as Iterable<any>) if (s && typeof s === 'object') out.push(s as Stat);
  }
  return out;
}

/** Coppia selezionata: transport.selectedCandidatePairId (Chrome/Safari), `selected` (Firefox), o la nominata riuscita. */
function selectedPair(stats: Stat[]): Stat | null {
  const pairs = stats.filter((s) => s.type === 'candidate-pair');
  if (pairs.length === 0) return null;
  const byId = new Map(pairs.map((p) => [p.id, p]));
  for (const t of stats) {
    if (t.type !== 'transport' || typeof t.selectedCandidatePairId !== 'string') continue;
    const p = byId.get(t.selectedCandidatePairId);
    if (p) return p;
  }
  return (
    pairs.find((p) => p.selected === true) ??
    pairs.find((p) => p.nominated === true && p.state === 'succeeded') ??
    null
  );
}

function relayVia(local: Stat): NetVia {
  const rp = typeof local.relayProtocol === 'string' ? local.relayProtocol.toLowerCase() : '';
  if (rp === 'tls') return 'turn-tls';
  if (rp === 'tcp') return 'turn-tcp';
  if (rp === 'udp') return 'turn-udp';
  // browser senza relayProtocol: si prova con l'URL del server TURN
  const url = typeof local.url === 'string' ? local.url.toLowerCase() : '';
  if (url.startsWith('turns:')) return 'turn-tls';
  if (url.includes('transport=tcp')) return 'turn-tcp';
  return 'turn-udp';
}

/**
 * Deriva direct/relay da un report di statistiche WebRTC.
 * @returns null se il report non ha una coppia selezionata o il candidato locale non è riconoscibile
 */
export function deriveNetPath(report: StatsLike | null | undefined): NetPath | null {
  if (!report) return null;
  let stats: Stat[];
  try {
    stats = toList(report);
  } catch {
    return null;
  }
  const pair = selectedPair(stats);
  if (!pair || typeof pair.localCandidateId !== 'string') return null;
  const local = stats.find((s) => s.type === 'local-candidate' && s.id === pair.localCandidateId);
  if (!local) return null;
  const type = typeof local.candidateType === 'string' ? local.candidateType : '';
  const protocol = typeof local.protocol === 'string' ? local.protocol.toLowerCase() : '';
  if (type === 'relay') return { net: 'relay', via: relayVia(local) };
  if (protocol === 'tcp') return { net: 'relay', via: 'tcp' };
  if (protocol === 'udp' && (type === 'host' || type === 'srflx' || type === 'prflx')) return { net: 'direct', via: 'udp' };
  return null;
}

/** Sorgente di statistiche: restituisce un report o undefined (può anche lanciare: viene gestito). */
export type StatsSource = () => Promise<StatsLike | undefined | null> | StatsLike | undefined | null;

/**
 * Prova le sorgenti in ordine e restituisce il primo percorso riconosciuto. Non lancia mai.
 */
export async function collectNetPath(sources: StatsSource[]): Promise<NetPath | null> {
  for (const src of sources) {
    try {
      const path = deriveNetPath(await src());
      if (path) return path;
    } catch {
      /* statistiche non disponibili da questa sorgente: si passa alla prossima */
    }
  }
  return null;
}

/** Forma minima (difensiva) di room.engine.pcManager in livekit-client 2.x. */
export interface EngineLike {
  pcManager?: {
    publisher?: { getStats?(): Promise<RTCStatsReport> | undefined } | null;
    subscriber?: { getStats?(): Promise<RTCStatsReport> | undefined } | null;
  } | null;
}

/** Sorgenti nell'ordine documentato in testa al file. */
export function netStatsSources(
  mic: { getRTCStatsReport?(): Promise<RTCStatsReport | undefined> } | null | undefined,
  engine: EngineLike | null | undefined,
): StatsSource[] {
  return [
    () => mic?.getRTCStatsReport?.(),
    () => engine?.pcManager?.publisher?.getStats?.(),
    () => engine?.pcManager?.subscriber?.getStats?.(),
  ];
}

/** Testo per title/aria-label, in italiano. */
export function describeNetPath(p: NetPath): string {
  if (p.net === 'direct') return 'Connessione diretta (UDP)';
  const how = p.via === 'tcp' ? 'TCP' : p.via === 'turn-udp' ? 'TURN/UDP' : p.via === 'turn-tcp' ? 'TURN/TCP' : 'TURN/TLS';
  return `Tramite server relay (${how}): un po' più di latenza`;
}
